<script setup lang="ts">
import BaseSelect from "@/core/components/base/BaseSelect.vue";
import LegacyAccess from "@/core/interfaces/Access";
import {inject, ref, watch} from "vue";
import {teamApi} from "@/container";
import {useToastStore} from "@/stores/toast.store";
import Rank from "@/core/interfaces/Rank";
import {reactiveOmit} from "@vueuse/core";
import {Input} from "@/core/components/ui/input";
import {Label} from "@/core/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from "@/core/components/ui/dialog";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {ShieldAlert} from "@lucide/vue";
import {UpdateAccessParams} from "@/core/http/resources/TeamResource";
import {rankRequiresRpps} from "@/team/planning";

const {member, ranks, team} = defineProps<{
    team?: LegacyAccess[],
    member: LegacyAccess,
    ranks: Rank[],
    operatorCodeOptions: { label: string; value: string }[]
}>();
const emits = defineEmits(['refresh', 'rank-changed', 'on-remove-operator-code']);
const {showSuccess, showError} = useToastStore();

const rpps = ref<string>(member?.rpps ?? member?.cps ?? "");
const rppsError = ref<boolean>(false);
const rppsFocused = ref<boolean>(false);
const openRankWarningModal = ref<boolean>(false);
const rankOptions = ref<{ label: string; value: string }[]>([{label: "Aucun", value: "null"}, ...ranks.map(item => {
    return {label: item.title, value: item.id.toString()};
})]);
const rankId = ref<string>(member.rank?.id ? member.rank.id.toString() : "null");
const operatorCode = ref<string>(member.operatorCode ?? "null");
const openConfirmationModal = inject('openConfirmationModal') as (
    title: string,
    message: string,
    success: () => never | Promise<void>,
    cancel?: () => never | Promise<void>
) => Promise<void>;

watch(() => member, () => {
    rankId.value = member.rank?.id ? member.rank.id.toString() : "null";
    operatorCode.value = member.operatorCode ?? "null";

    // A team refetch must not blank a RPPS the user is typing.
    if (!rppsFocused.value) {
        rpps.value = member?.rpps ?? member?.cps ?? "";
    }
});

/**
 * Tell if an operator code is already affected to a team member.
 */
const operatorCodeIsAffected = (currentMember: LegacyAccess, operatorCode: string): boolean => {
    const memberWithOperatorCode = team?.find(member => member.operatorCode === operatorCode);

    return !!memberWithOperatorCode && memberWithOperatorCode.id !== currentMember.id;
};

/**
 * Update the team member access data.
 */
const update = (teamMember: LegacyAccess) => {
    const payload: UpdateAccessParams = {...teamMember};
    // If the member has no user in db (api has returned 0), we send null to avoid linking a non-existing user.
    if (teamMember.userId === 0) {
        payload.userId = null;
    }

    if (teamMember?.operatorCode && operatorCodeIsAffected(teamMember, teamMember.operatorCode)) {
        openConfirmationModal(
            "Ce code opérateur est déjà affecté à un autre membre de votre équipe",
            "Voulez vous vraiment affecter le code à cette personne ?",
            async () => {
                emits('on-remove-operator-code', teamMember.operatorCode);
                return teamApi().updateAccess(payload)
                    .then(() => showSuccess({content: 'Les informations ont été correctement mises à jour'}))
                    .catch(() => showError({content: 'Les informations n\'ont pas pu être mises à jour'}));
            },
            () => {
                operatorCode.value = "null";

                return Promise.resolve();
            }
        );

        return;
    } else {
        return teamApi().updateAccess(payload)
            .then(() => showSuccess({content: 'Les informations ont été correctement mises à jour'}))
            .catch(() => showError({content: 'Les informations n\'ont pas pu être mises à jour'}));
    }
};

watch(() => rankId.value, async () => {
    const rank = ranks.find(rank => rank.id === Number(rankId.value));
    const memberWithoutRankId = reactiveOmit(member, "rankId");
    if (rank && rank.id !== member.rankId) {
        const updatedMember: LegacyAccess = {...memberWithoutRankId, rank: rank, rankId: rank.id};
        await update(updatedMember);
        emits('rank-changed', rank.id);
    }
});

watch(() => operatorCode.value, () => {
    if (operatorCode.value === (member.operatorCode ?? "null")) {
        return;
    }

    const updatedMember: LegacyAccess = {...member, operatorCode: operatorCode.value};
    update(updatedMember);
});

/**
 * Action on rank id update.
 */
const onRankIdUpdate = (id: string) => {
    // "Aucun" is a display placeholder: the update endpoint has no rank
    // clearing, so refuse it loudly instead of silently changing nothing.
    if (id === 'null') {
        showError({content: 'Choisissez un poste — le retrait d\'un poste passe par le support'});

        return;
    }

    // A titulaire rank can only be affected to a member with a linked user (with mail and password).
    if ((id === '1') && !member.userId) {
        openRankWarningModal.value = true;

        return;
    }

    const holders = team?.filter(member => member.rankId === 1) ?? [];

    if (member.rankId === 1 && holders.length === 1) {
        showError({content: 'Vous devez avoir au moins un titulaire dans votre pharmacie'});
    } else {
        rankId.value = id;
    }
};

/**
 * Action on rpps blur: save only what actually changed, so a mere
 * focus-out fires no request and paints no error.
 */
const onRppsUpdate = async () => {
    rppsFocused.value = false;

    if (rpps.value === (member?.rpps ?? member?.cps ?? "")) {
        rppsError.value = false;

        return;
    }

    if (!/^\d{11}$/.test(rpps.value)) {
        rppsError.value = true;
    } else {
        rppsError.value = false;
        await update({...member, rpps: rpps.value});
        emits('refresh');
    }
};
</script>

<template>
  <div class="flex flex-col gap-4 rounded-lg border border-gray-100 bg-gray-50/60 p-3.5">
    <div class="text-xs font-semibold text-gray-500">
      Accès &amp; identification
    </div>
    <div class="grid grid-cols-2 gap-4">
      <div class="flex flex-col gap-1.5">
        <Label>Poste</Label>
        <BaseSelect
          :model-value="rankId"
          trigger-class="items-start w-full"
          :options="rankOptions"
          @update:model-value="onRankIdUpdate"
        />
      </div>
      <div class="flex flex-col gap-1.5">
        <Label>Code opérateur</Label>
        <BaseSelect
          v-model:model-value="operatorCode"
          trigger-class="items-start w-full"
          :options="operatorCodeOptions"
        />
      </div>
    </div>
    <div v-if="rankRequiresRpps(Number(rankId))" class="flex flex-col gap-1.5">
      <Label>Numéro RPPS</Label>
      <div class="flex items-center gap-2">
        <Input
          v-model="rpps"
          placeholder="XXXXXXXXXXX"
          :class="rppsError ? 'border-red-400 focus-visible:ring-red-400' : ''"
          @focus="rppsFocused = true"
          @blur="onRppsUpdate"
        />
      </div>
      <p v-if="rppsError" class="flex items-center gap-1 text-xs text-red-500">
        <ShieldAlert class="size-3.5 shrink-0" />
        Doit contenir exactement 11 chiffres
      </p>
    </div>

    <Dialog v-model:open="openRankWarningModal">
      <DialogContent>
        <DialogHeader>
          <DialogTitle class="flex items-center gap-2">
            <ShieldAlert class="size-5 text-amber-500" />
            Action impossible
          </DialogTitle>
          <DialogDescription>
            Veuillez contacter le support pour changer de titulaire.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <BaseButton primary @click="openRankWarningModal = false">
            J'ai compris
          </BaseButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>
